import { alertVariants } from "@visiblethread/react-components";
import localhost, { downloadFileFromResponse } from "apis/localhost";
import { userAdminImportUsersFromCsvConfirmationModal } from "components/Modal/confModalTypes";
import { MULTIPART_FORM_DATA_CONTENT_TYPE } from "constants/app";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  clearJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { removeErrorMessage } from "slices/app/errorSlice";
import { setWorkspacesInfo } from "slices/systemAdmin/systemAdminSlice";
import {
  setUsersInfo,
  setWorkspaceInfo,
} from "slices/userAdmin/userAdminSlice";

export const fetchWorkspaceInfo = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/userAdmin/getWorkspace")
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspaceInfo(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const userAdminRemoveUserFromWorkspace =
  (userName, workspaceId, workspaceName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .delete("/userAdmin/workspace/user", {
        data: { userName },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(removeErrorMessage);
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(setWorkspaceInfo(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `${userName} successfully removed from ${workspaceName}`,
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const createUserAndAddToWorkspace =
  (
    userName,
    firstName,
    lastName,
    password,
    licensed,
    readabilitySettingsAdmin,
    acronymSettingsAdmin,
    discoverySettingsAdmin,
    excelOutputsAdmin,
    dictionariesAdmin,
    proposalStagesAdmin,
    docStyleTemplatesAdmin,
    editCurrenciesAdmin,
    archiveStatusesAdmin,
    editCustomFieldsAdmin,
    shreddingTemplatesAdmin,
    customCategoriesAdmin,
    userAdmin
  ) =>
  async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post("/userAdmin/workspace/user", {
        userName,
        firstName,
        lastName,
        password,
        licensed,
        readabilitySettingsAdmin,
        acronymSettingsAdmin,
        discoverySettingsAdmin,
        excelOutputsAdmin,
        dictionariesAdmin,
        proposalStagesAdmin,
        docStyleTemplatesAdmin,
        editCurrenciesAdmin,
        archiveStatusesAdmin,
        editCustomFieldsAdmin,
        shreddingTemplatesAdmin,
        customCategoriesAdmin,
        userAdmin,
      })
      .then(async (response) => {
        const { data } = response;

        dispatch(setWorkspaceInfo(data));
        dispatch(hideModal(modalTypes.NEW_USER));

        if (
          data.workspaces[0].users.filter(
            (user) => user.username === userName
          )[0].licensed
        ) {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: `${userName} has been created and has been added to the ${data.workspaces[0].name} workspace as a “Licensed User“`,
            })
          );
        } else if (userAdmin) {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: `${userName} was added with admin permissions only. They won’t use a license seat.`,
            })
          );
        } else {
          dispatch(
            showGlobalAlert({
              variant: alertVariants.success,
              text: `${userName} was added as a member with no permissions. Assign a role to give them access.`,
            })
          );
        }
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.NEW_USER));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const importUsersWithRolesFromCsv = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/userAdmin/importUsers", formData, {
      headers: {
        "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setWorkspaceInfo(data.workspacesResponse));
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          userAdminImportUsersFromCsvConfirmationModal(
            data.usersReportString,
            data.message,
            dispatch
          )
        )
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const exportResultFromImportUsers =
  (results, isCloudAdmin) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .get(`/${isCloudAdmin ? "cloudAdmin" : "userAdmin"}/exportUsersResult`, {
        responseType: "blob",
        params: { usersString: results },
      })
      .then(async (response) => {
        const { data } = response;

        downloadFileFromResponse(data, "BulkUserResults.csv");

        dispatch(hideModal(modalTypes.CONFIRMATION));
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const editUser =
  (
    workspaceId,
    userName,
    firstName,
    lastName,
    password,
    licensed,
    userAdmin,
    readabilitySettingsAdmin,
    acronymSettingsAdmin,
    discoverySettingsAdmin,
    excelOutputsAdmin,
    dictionariesAdmin,
    proposalStagesAdmin,
    docStyleTemplatesAdmin,
    editCurrenciesAdmin,
    archiveStatusesAdmin,
    editCustomFieldsAdmin,
    shreddingTemplatesAdmin,
    customCategoriesAdmin
  ) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));
    let url;
    const admin =
      getState().app.session.userInfo.roles.includes("system_admin");
    if (admin) {
      url = `/cloudAdmin/workspace/${workspaceId}/user`;
    } else {
      url = "/userAdmin/workspace/user";
    }
    const currentUserName = getState().app?.session?.userInfo.username;

    await localhost
      .put(url, {
        userName,
        firstName,
        lastName,
        password,
        licensed,
        userAdmin,
        readabilitySettingsAdmin,
        acronymSettingsAdmin,
        discoverySettingsAdmin,
        excelOutputsAdmin,
        dictionariesAdmin,
        proposalStagesAdmin,
        docStyleTemplatesAdmin,
        editCurrenciesAdmin,
        archiveStatusesAdmin,
        editCustomFieldsAdmin,
        shreddingTemplatesAdmin,
        customCategoriesAdmin,
      })
      .then(async (response) => {
        const { data } = response;
        // session is invalid after updating the current user
        if (currentUserName === userName) {
          window.location.href = "/#/login/permissions-updated";
        }

        if (admin) {
          dispatch(setWorkspacesInfo(data));
        } else {
          dispatch(setWorkspaceInfo(data));
        }
        dispatch(hideModal(modalTypes.EDIT_USER_AND_PERMISSIONS));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: "User Successfully Updated",
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.EDIT_USER_AND_PERMISSIONS));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

const getAddUsersToWorkspaceResultBanner = (
  users,
  currentLicensedUsers,
  response
) => {
  const username = users[0];
  const workspaceName = response.workspaces[0].name;
  const workspaceReservedLicenses = response.workspaces[0].reservedLicenses;
  const { licensed } = response.workspaces[0].users.find(
    (user) => user.username === username
  );

  if (licensed) {
    return {
      variant: alertVariants.success,
      text: `The selected user/s have been successfully added to the ${workspaceName} workspace as Licensed user/s.`,
    };
  }
  if (workspaceReservedLicenses - currentLicensedUsers - users.length < 0) {
    return {
      variant: alertVariants.warning,
      text: `The selected user/s have been successfully added to the ${workspaceName} workspace but have not been licensed. You have reached the maximum number of licensed users for this workspace. Please de-activate a different user and try again.`,
    };
  }
  return {
    variant: alertVariants.warning,
    text: `The selected user/s have been successfully added to the ${workspaceName} workspace but have not been licensed. You have reached the maximum number of licensed users. Please de-activate a different user and try again.`,
  };
};

export const addUsersToWorkspace =
  (users, currentLicensedUsers) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .put("/userAdmin/workspace/users", { users })
      .then(async (response) => {
        const { data } = response;
        const bannerOptions = getAddUsersToWorkspaceResultBanner(
          users,
          currentLicensedUsers,
          data
        );
        dispatch(setWorkspaceInfo(data));
        dispatch(hideModal(modalTypes.ADD_USER_TO_WORKSPACE));
        dispatch(showGlobalAlert(bannerOptions));
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const getUsers = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/userAdmin/users")
    .then(async (response) => {
      const { data } = response;
      dispatch(setUsersInfo(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};
